/**
 * loanChangelogConfig.js
 *
 * Metadata + raw-event → display helpers for the <LoanChangelog> timeline.
 * The timeline renders the raw structured audit events
 * (GET /underwriting/loan_events/{dealId}) directly — these helpers turn each
 * raw event into a plain-language action, a one-line summary, and per-field
 * detail rows.
 *
 * Raw event shape (every field optional — helpers degrade gracefully):
 *   {
 *     event_type:           "exception_updates",       // ~= the audit topic
 *     sub_event:            "create_liquidity_source",  // verb + object
 *     changed_fields:       { sourceType, amount, updateDateTime, … },
 *     changed_by:           "jimmy@example.com",
 *     changed_by_name:      "Jimmy Oliva",
 *     timestamp:            "2026-06-17T17:29:45.669102", // UTC, no zone suffix
 *     loan_status_at_event: "Prospect",
 *     status_change:        "Open" | { from, to },
 *   }
 *
 * Icons are MUI (lucide isn't a dependency) but keep the lucide visual roles.
 */
import {
  AddCircleOutlineOutlined,
  AttachMoneyOutlined,
  BusinessOutlined,
  ChatBubbleOutlineOutlined,
  CheckBoxOutlined,
  CloudSyncOutlined,
  DescriptionOutlined,
  EventOutlined,
  FactCheckOutlined,
  FlagOutlined,
  GavelOutlined,
  GroupOutlined,
  HandshakeOutlined,
  HistoryOutlined,
  HomeOutlined,
  LocalShippingOutlined,
  LockOutlined,
  NoteAltOutlined,
  SyncOutlined,
  TuneOutlined,
  VisibilityOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import { addressLabel } from "../../../../../_src_shared/utils/functions/addressLabel";

// event_type -> visual + label metadata. `Icon` is a MUI icon component.
export const TYPE_META = {
  loan_view: { label: "Viewed", Icon: VisibilityOutlined, accent: "#8A93A6", tint: "#F0F2F6" },
  comments: { label: "Comments", Icon: ChatBubbleOutlineOutlined, accent: "#0F766E", tint: "#DEF2EF" },
  loan_stage: { label: "Status", Icon: FlagOutlined, accent: "#4F46E5", tint: "#ECECFE" },
  loan_documents: { label: "Documents", Icon: DescriptionOutlined, accent: "#2563EB", tint: "#E8F0FE" },
  borrowers: { label: "Borrowers", Icon: GroupOutlined, accent: "#7C3AED", tint: "#F1EBFE" },
  underwriting_conditions: { label: "Underwriting", Icon: FactCheckOutlined, accent: "#D97706", tint: "#FCF1E2" },
  loan_updates: { label: "Loan Updates", Icon: TuneOutlined, accent: "#0F9D7E", tint: "#E2F5F0" },
  deal_terms: { label: "Deal Terms", Icon: AttachMoneyOutlined, accent: "#0F9D7E", tint: "#E2F5F0" },
  exception_updates: { label: "Exceptions", Icon: WarningAmberOutlined, accent: "#DC2626", tint: "#FCE9E9" },
  subject_properties: { label: "Property", Icon: HomeOutlined, accent: "#C2410C", tint: "#FBEBE0" },
  task_updates: { label: "Tasks", Icon: CheckBoxOutlined, accent: "#4D7C0F", tint: "#EFF6E0" },
  // Topics the listener also emits (structured_audit registers on ALL_TOPICS).
  // Without these they'd fall back to the generic "Activity" label.
  loan_lock: { label: "Loan Lock", Icon: LockOutlined, accent: "#7C3AED", tint: "#F1EBFE" },
  entities: { label: "Entities", Icon: BusinessOutlined, accent: "#6D28D9", tint: "#F1EBFE" },
  loan_closing: { label: "Closing", Icon: HandshakeOutlined, accent: "#0F766E", tint: "#DEF2EF" },
  underwriting_notes: { label: "UW Notes", Icon: NoteAltOutlined, accent: "#D97706", tint: "#FCF1E2" },
  key_dates: { label: "Key Dates", Icon: EventOutlined, accent: "#2563EB", tint: "#E8F0FE" },
  appraisals: { label: "Appraisal", Icon: GavelOutlined, accent: "#C2410C", tint: "#FBEBE0" },
  pricing_updates: { label: "Pricing", Icon: AttachMoneyOutlined, accent: "#0F9D7E", tint: "#E2F5F0" },
  loan_creation: { label: "Loan Created", Icon: AddCircleOutlineOutlined, accent: "#4F46E5", tint: "#ECECFE" },
  create_loan: { label: "Loan Created", Icon: AddCircleOutlineOutlined, accent: "#4F46E5", tint: "#ECECFE" },
  loan_details: { label: "Loan Updates", Icon: TuneOutlined, accent: "#0F9D7E", tint: "#E2F5F0" },
  order_updates: { label: "Orders", Icon: LocalShippingOutlined, accent: "#2563EB", tint: "#E8F0FE" },
  external_orders: { label: "Orders", Icon: LocalShippingOutlined, accent: "#2563EB", tint: "#E8F0FE" },
  title_reports: { label: "Title", Icon: LocalShippingOutlined, accent: "#2563EB", tint: "#E8F0FE" },
  appraisals_order: { label: "Appraisal", Icon: GavelOutlined, accent: "#C2410C", tint: "#FBEBE0" },
  // Integration syncs (Box → LOS, Salesforce, etc.) — the recent listener work.
  box_sync: { label: "Box Sync", Icon: CloudSyncOutlined, accent: "#8A93A6", tint: "#F0F2F6" },
  loan_sync: { label: "Sync", Icon: SyncOutlined, accent: "#8A93A6", tint: "#F0F2F6" },
  chatter_sync: { label: "Sync", Icon: SyncOutlined, accent: "#8A93A6", tint: "#F0F2F6" },
  lock_sync: { label: "Sync", Icon: SyncOutlined, accent: "#8A93A6", tint: "#F0F2F6" },
  exception_sync: { label: "Sync", Icon: SyncOutlined, accent: "#8A93A6", tint: "#F0F2F6" },
  fee_sync: { label: "Sync", Icon: SyncOutlined, accent: "#8A93A6", tint: "#F0F2F6" },
};

// Fallback metadata for any unmapped event_type.
export const FALLBACK_META = { label: "Activity", Icon: HistoryOutlined, accent: "#0F9D7E", tint: "#E2F5F0" };

export const metaFor = (eventType) => TYPE_META[eventType] || FALLBACK_META;

export const FIELD_LABELS = {
  sourceType: "Source type", amount: "Amount", countedPercentage: "Counted %",
  verified: "Verified", unlinkedPartyName: "Party", firstName: "First name",
  lastName: "Last name", emailAddress: "Email", phone: "Phone", maritalStatus: "Marital status",
  citizenshipStatus: "Citizenship", isGuarantor: "Guarantor", company: "Company",
  loanRole: "Role", loanProductType: "Product", loanPurpose: "Purpose",
  delayedPurchase: "Delayed purchase",
  salesperson: "Salesperson", salesAnalyst: "Sales analyst", status: "Status",
  requirementType: "Requirement", docGroup: "Group", docType: "Doc type",
  docStipStatus: "Stip status", escrowCategory: "Escrow", escrowSubCategory: "Sub-category",
  paidTo: "Paid to", dueDateFrequency: "Frequency", nextDueDate: "Next due",
  exceptionStatus: "Status", exceptionDescription: "Description", exceptionCategory: "Category",
  riskBucket: "Risk bucket", additional_borrower_notes: "Notes", borrowerIds: "Linked borrowers",
};

// Internal bookkeeping + structural keys never shown as plain detail rows
// (the structural ones — comments, uploads — render as their own rich blocks).
export const HIDDEN_DETAIL_KEYS = new Set([
  "updateDateTime",
  "htmlString",
  "associations",
  "comments",
  "uploadMetadata",
  "documents_updated",
  "sendEmail",
  "addToMainComments",
  "status_change", // nested milestone object — rendered as a from→to transition
]);

const ACTION_MAP = {
  loan_status_update: "Changed loan status",
  new_comment: "Added a comment",
  create_comment: "Added a comment",
  edit_comment: "Edited a comment",
  delete_comment: "Deleted a comment",
  view_file: "Opened a file",
  upload_loan_docs: "Uploaded a document",
  update_loan_doc_metadata: "Updated document details",
  create_borrower: "Added a borrower",
  update_borrower: "Updated a borrower",
  create_liquidity_source: "Added a liquidity source",
  update_liquidity_source: "Updated a liquidity source",
  create_underwriting_requirement: "Created a condition",
  update_underwriting_requirement: "Updated a condition",
  create_exception_request: "Opened an exception",
  set_escrow_item: "Set an escrow item",
  update_subject_property: "Updated the property",
  add_loan_related_contact: "Added a contact",
  add_permitted_users: "Granted access",
  create_uw_tasklist: "Created the UW task list",
  get_loan_terms: "Pulled loan terms",
  _id: "Edited loan details",
};

// Singular object noun per event_type — used to build a readable action phrase
// ("Updated an exception") for any sub_event we don't have an explicit map for,
// instead of falling back to the bare category label ("Exceptions").
const TYPE_NOUN = {
  exception_updates: "exception",
  underwriting_conditions: "condition",
  loan_documents: "document",
  borrowers: "borrower",
  subject_properties: "property",
  deal_terms: "term",
  task_updates: "task",
  comments: "comment",
  loan_stage: "loan status",
  loan_updates: "loan detail",
  loan_lock: "lock",
  entities: "entity",
  loan_closing: "closing detail",
  underwriting_notes: "note",
  key_dates: "key date",
  appraisals: "appraisal",
  pricing_updates: "price",
  loan_details: "loan detail",
};

const VERB_RULES = [
  [/^(create|add|new)/, "Added"],
  [/^(update|edit|change|set)/, "Updated"],
  [/^(delete|remove)/, "Removed"],
  [/^(approve)/, "Approved"],
  [/^(deny|reject|decline)/, "Denied"],
  [/^(assign|reassign)/, "Assigned"],
  [/^(upload)/, "Uploaded"],
];

const verbFor = (sub = "") => {
  const s = String(sub).toLowerCase();
  for (const [re, label] of VERB_RULES) if (re.test(s)) return label;
  return null;
};

const article = (noun = "") => (/^[aeiou]/i.test(noun) ? "an" : "a");

export const actionPhrase = (ev) => {
  if (ev?.event_type === "loan_view") return "Viewed the loan";
  if (ACTION_MAP[ev?.sub_event]) return ACTION_MAP[ev.sub_event];
  // Derive a sensible "<Verb> a/an <noun>" from the sub_event + event_type.
  const verb = verbFor(ev?.sub_event);
  const noun = TYPE_NOUN[ev?.event_type];
  if (verb && noun) return `${verb} ${article(noun)} ${noun}`;
  if (verb) return verb;
  return metaFor(ev?.event_type).label;
};

// A milestone/status transition with both ends, e.g.
// { from: "Clear To Close", to: "Closed" }. Pulls from the nested
// changed_fields.status_change object (loan_stage events) or a top-level
// { from, to } object. Returns null when there's no clear two-sided transition.
export const statusTransition = (ev) => {
  const pick = (o) => {
    if (!o || typeof o !== "object") return null;
    const from = o.priorMilestoneStatus ?? o.from ?? o.prior ?? o.priorStatus;
    const to = o.updatedMilestoneStatus ?? o.to ?? o.updated ?? o.newStatus;
    return to ? { from: from || null, to } : null;
  };
  return pick(ev?.changed_fields?.status_change) || pick(ev?.status_change) || null;
};

// Plain-language description of a status_change ("Status set to In Review",
// "Pending → Approved"). Returns "" when there's no status change.
export const statusLine = (ev) => {
  const sc = ev?.status_change;
  if (typeof sc === "string" && sc.trim()) return `Status set to ${sc}`;
  if (sc && typeof sc === "object") {
    const from = sc.from ?? sc.prior ?? sc.priorMilestoneStatus;
    const to = sc.to ?? sc.updated ?? sc.updatedMilestoneStatus ?? sc.newStatus;
    if (to) return from ? `${from} → ${to}` : `Status set to ${to}`;
  }
  return "";
};

const dollar = (n) => `$${Number(n ?? 0).toLocaleString()}`;

export const summaryLine = (ev) => {
  const f = ev?.changed_fields || {};
  // Comments, uploads, and status changes render their own rich block.
  if (ev?.event_type === "comments") return "";
  if (ev?.event_type === "loan_stage") return "";
  if (ev?.sub_event === "upload_loan_docs") return "";
  switch (ev?.sub_event) {
    case "create_borrower":
      return `${f.firstName ?? ""} ${f.lastName ?? ""}`.trim();
    case "create_liquidity_source":
      return `${f.sourceType ?? ""} · ${dollar(f.amount)}`;
    case "upload_loan_docs":
      return f.uploadMetadata?.[0]?.file_display_name ?? "";
    case "update_loan_doc_metadata":
      return f.documents_updated?.[0]?.docType ?? "";
    case "create_underwriting_requirement":
      return f.docType ?? f.requirementType ?? "";
    case "set_escrow_item":
      return `${f.escrowSubCategory ?? ""} → ${f.paidTo ?? ""}`;
    case "create_exception_request":
      return f.exceptionDescription ?? "";
    case "update_subject_property":
      return addressLabel(f.address) ?? "Property details";
    case "add_loan_related_contact":
      return `${f.firstName ?? ""} ${f.lastName ?? ""} · ${f.loanRole ?? ""}`.replace(/^ · | · $/g, "");
    case "_id":
      return Object.keys(f)
        .filter((k) => !HIDDEN_DETAIL_KEYS.has(k))
        .map((k) => FIELD_LABELS[k] ?? k)
        .join(", ");
    default:
      // No type-specific summary — if this was a status change, say so plainly.
      return statusLine(ev);
  }
};

export const initials = (name = "") =>
  name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

// Deterministic avatar hue per person.
export const hueFor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
};

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T/;
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
// The LOS models type date-ONLY fields (`form_type: 'date'` — closing dates,
// appraisal dates, lease dates…) as `dt.datetime`, so they serialize as
// "2026-08-27T00:00:00". Midnight-UTC is the tell: those carry no meaningful
// time-of-day and must render via formatDate, or Eastern conversion walks them
// back a day (Aug 27 → Aug 26). Anything with a real clock time is a genuine
// timestamp and goes through formatDateTime.
const MIDNIGHT_RE = /T00:00(:00(\.0+)?)?(Z|[+-]00:00)?$/;

// Render a changed-field value for the detail rows (timezone-correct for dates).
export const fmtVal = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "string") {
    if (DATE_ONLY_RE.test(v)) return formatDate(v);
    if (ISO_DATETIME_RE.test(v))
      return MIDNIGHT_RE.test(v) ? formatDate(v) : formatDateTime(v, "date");
    return v;
  }
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
  if (typeof v === "object") {
    const label = addressLabel(v);
    if (label) return label;
    return JSON.stringify(v);
  }
  return String(v);
};

// Detail key/value pairs for a raw event, minus internal bookkeeping keys.
export const detailFields = (ev) =>
  Object.entries(ev?.changed_fields || {}).filter(([k]) => !HIDDEN_DETAIL_KEYS.has(k));

// --- Comment rendering ---------------------------------------------------
// Returns the comment body HTML for a `comments` event (or an exception's
// inline first comment), with the verbose "@Name <email>" mention text reduced
// to just "@Name". Returns null when there's no comment body.
export const commentHtml = (ev) => {
  let html = null;
  if (ev?.event_type === "comments") html = ev?.changed_fields?.htmlString;
  // Any event that carries an inline htmlString body renders like a comment —
  // e.g. an underwriting condition (create_underwriting_requirement) whose text
  // lives in changed_fields.htmlString. This keeps conditions, notes, etc.
  // reading as rich blocks rather than raw field rows.
  else if (typeof ev?.changed_fields?.htmlString === "string")
    html = ev.changed_fields.htmlString;
  else if (Array.isArray(ev?.changed_fields?.comments))
    html = ev.changed_fields.comments[0]?.htmlString;
  if (!html || typeof html !== "string") return null;
  // Strip the escaped "<email>" that follows a @mention's display name.
  return html.replace(/\s*&lt;[^&]*?&gt;/g, "");
};

// --- Document previews ---------------------------------------------------
// Returns the previewable documents touched by an event (uploads or metadata
// edits). Each item carries what LoanFilePreviewModal needs: `_id` (used to
// resolve the full doc from redux) plus any inline metadata we already have.
export const previewableDocs = (ev) => {
  // A "view_file" event records no metadata — recover the file from its
  // source_endpoint: …/files/file_by_id/{dealId}/{file_storage_name}
  if (ev?.sub_event === "view_file") {
    const parts = String(ev?.source_endpoint || "").split("?")[0].split("/").filter(Boolean);
    const storage = parts[parts.length - 1];
    const dealSeg = parts[parts.length - 2];
    if (storage && /\.[^.]+$/.test(storage)) {
      return [
        {
          _id: storage.replace(/\.[^.]+$/, ""),
          file_storage_name: storage,
          dealId: dealSeg,
          file_display_name: storage,
        },
      ];
    }
    return [];
  }
  const f = ev?.changed_fields || {};
  const list = Array.isArray(f.uploadMetadata)
    ? f.uploadMetadata
    : Array.isArray(f.documents_updated)
      ? f.documents_updated
      : [];
  return list
    .filter((d) => d && d._id)
    .map((d) => ({
      _id: d._id,
      file_storage_name: d.file_storage_name,
      content_type: d.content_type,
      file_display_name: d.file_display_name || d.docType || "Document",
      docType: d.docType,
      docGroup: d.docGroup,
    }));
};

// All documents touched by an event, for the activity card's file list. A
// superset of `previewableDocs`: integration syncs (e.g. box_sync's
// `box_correspondent_sync`) record an `uploadMetadata` array with the doc's
// group / type / filename but NO `_id`, so those rows render read-only (no
// preview) while still surfacing what was uploaded. Rows that DO carry an
// `_id` (in-app uploads, metadata edits, view_file) stay clickable.
export const eventDocuments = (ev) => {
  // view_file events recover a single clickable doc from their source_endpoint.
  const previewable = previewableDocs(ev);
  if (ev?.sub_event === "view_file") {
    return previewable.map((d, i) => ({
      key: d._id || `${i}`,
      previewable: true,
      ...d,
    }));
  }
  const f = ev?.changed_fields || {};
  const list = Array.isArray(f.uploadMetadata)
    ? f.uploadMetadata
    : Array.isArray(f.documents_updated)
      ? f.documents_updated
      : [];
  return list.filter(Boolean).map((d, i) => ({
    key: d._id || `${d.file_display_name || d.docType || "doc"}-${i}`,
    _id: d._id || null,
    // Only docs with an _id can be resolved from the loan + previewed.
    previewable: !!d._id,
    file_storage_name: d.file_storage_name,
    content_type: d.content_type,
    file_display_name: d.file_display_name || d.docType || "Document",
    docType: d.docType || null,
    docGroup: d.docGroup || null,
    // Metadata-edit events (update_loan_doc_metadata) carry the changed value,
    // e.g. { documentStatus: "Accepted" }, so the row can show what changed.
    documentStatus: d.documentStatus || null,
  }));
};
